import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { studyGroupMembershipApi } from "@/lib/humhub-api/studyGroupMembership";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";

export const useStudyGroupMentionAvailableUsers = (
  studyGroupId: string | number | null,
  options: {
    topicIds?: Array<string | number>;
  } = {},
) => {
  const [keyword, setKeyword] = useState<string | undefined>(undefined);
  const queryClient = useQueryClient();
  const topicIds = useMemo(() => {
    return options.topicIds
      ?.map((topicId) => Number(topicId))
      .filter((topicId) => Number.isFinite(topicId) && topicId > 0)
      .map(String);
  }, [options.topicIds]);
  const mentionScopeKey = useMemo(
    () => [studyGroupId ?? "", topicIds?.join(",") ?? ""].join(":"),
    [studyGroupId, topicIds],
  );
  const fetchMentionAvailableUsers = useCallback(
    (searchKeyword?: string) => {
      if (!studyGroupId) {
        throw new Error("Study group ID is null");
      }
      return studyGroupMembershipApi.getMentionAvailableUsers(studyGroupId, {
        keyword: searchKeyword,
        topicIds,
      });
    },
    [studyGroupId, topicIds],
  );

  // React Queryでデータ取得
  const {
    data,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: studyGroupKeys.mentionAvailableUsers(
      studyGroupId,
      keyword,
      topicIds,
    ),
    queryFn: () => fetchMentionAvailableUsers(keyword),
    enabled: !!studyGroupId,
    staleTime: 3 * 60 * 1000,
  });

  // 検索用の関数（即座に結果を返す）
  const searchMentionAvailableUsers = useCallback(
    async (newKeyword?: string) => {
      if (!studyGroupId) {
        return [];
      }

      try {
        // queryClientを使って直接データを取得
        const result = await queryClient.fetchQuery({
          queryKey: studyGroupKeys.mentionAvailableUsers(
            studyGroupId,
            newKeyword,
            topicIds,
          ),
          queryFn: () => fetchMentionAvailableUsers(newKeyword),
          staleTime: 3 * 60 * 1000,
        });

        // 状態も更新
        setKeyword(newKeyword);

        return result || []; // ← result?.results から result に変更
      } catch (error) {
        console.error("Search failed:", error);
        return [];
      }
    },
    [fetchMentionAvailableUsers, queryClient, studyGroupId, topicIds],
  );

  const fetchPage = useCallback(
    async (params?: { keyword?: string }) => {
      return await searchMentionAvailableUsers(params?.keyword);
    },
    [searchMentionAvailableUsers],
  );

  return {
    data: data || [], // ← data?.results から data に変更
    pagination: undefined, // ← ページネーション情報は不要
    isLoading,
    error: queryError ? String(queryError) : null,
    fetchPage,
    searchMentionAvailableUsers,
    mentionScopeKey,
  };
};
